import React, { useMemo, useState } from "react";
import {
    Alert,
    Button,
    Card,
    Empty,
    Popconfirm,
    Space,
    Table,
    Tag,
    Tooltip,
    Typography,
    message,
    theme,
} from "antd";
import {
    DeleteOutlined,
    EditOutlined,
    LineChartOutlined,
    LockOutlined,
    PartitionOutlined,
    PlayCircleOutlined,
    PlusOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { useIntl } from "react-intl";
import { useScopedNavigate } from "../components/MarketerView/useScopedNavigate";
import { useAuth } from "../hooks/useAuth";
import {
    useCreateSavedSegmentMutation,
    useDeleteSavedSegmentMutation,
    useEvaluateSavedSegmentMutation,
    useGetActiveSegmentationFieldsQuery,
    useGetOrganisationEntitlementsQuery,
    useGetSavedSegmentsQuery,
    useGetSegmentCriteriaOptionsQuery,
    useGetSegmentLibraryUsageQuery,
    useUpdateSavedSegmentMutation,
} from "../services/nestjs-base-api";
import {
    ISavedSegment,
    ISegmentSurveyCriteria,
    ISegmentationField,
    ISurveyCohort,
} from "../services/nestjs-base-api";
import { SegmentEditorModal } from "../components/customers/SegmentEditorModal";

const { Title, Text, Paragraph } = Typography;

const countSurveyCriteria = (criteria: ISegmentSurveyCriteria | null | undefined): number => {
    if (!criteria) return 0;
    return (criteria.cohortIds?.length ?? 0) + (criteria.countryCodes?.length ?? 0);
};

const CustomerSegmentsPage: React.FunctionComponent = () => {
    const intl = useIntl();
    const navigate = useScopedNavigate();
    const { user } = useAuth();
    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();
    const jwtToken = user?.jwtToken ?? "";

    const { data: segments, isLoading: listLoading, refetch } = useGetSavedSegmentsQuery(
        { jwtToken },
        { skip: !jwtToken },
    );
    const { data: fieldsData } = useGetActiveSegmentationFieldsQuery(
        { jwtToken },
        { skip: !jwtToken },
    );
    const { data: criteriaOptions } = useGetSegmentCriteriaOptionsQuery(
        { jwtToken },
        { skip: !jwtToken },
    );
    const { data: usage } = useGetSegmentLibraryUsageQuery(
        { jwtToken },
        { skip: !jwtToken },
    );
    const orgId = user?.organisation?.id;
    const { data: entitlements } = useGetOrganisationEntitlementsQuery(
        { jwtToken, orgId: String(orgId ?? "") },
        { skip: !jwtToken || !orgId },
    );
    const canCompareOnTrends = useMemo(
        () =>
            (entitlements ?? []).some(
                (e) => e.feature === "SEGMENT_COMPARISON_ACCESS" && e.hasAccess,
            ),
        [entitlements],
    );

    const fields = useMemo<ISegmentationField[]>(() => fieldsData ?? [], [fieldsData]);
    const cohorts = useMemo<ISurveyCohort[]>(() => criteriaOptions?.cohorts ?? [], [criteriaOptions]);
    const countries = useMemo<string[]>(() => criteriaOptions?.countries ?? [], [criteriaOptions]);

    const [createSegment, { isLoading: creating }] = useCreateSavedSegmentMutation();
    const [updateSegment, { isLoading: updating }] = useUpdateSavedSegmentMutation();
    const [deleteSegment] = useDeleteSavedSegmentMutation();
    const [evaluateSegment, evalState] = useEvaluateSavedSegmentMutation();

    const [editorOpen, setEditorOpen] = useState(false);
    const [editing, setEditing] = useState<ISavedSegment | null>(null);

    // Library limit enforcement, mirroring the backend cap.
    const libraryFull = useMemo(() => {
        if (!usage) return false;
        if (usage.unlimited) return false;
        return usage.used >= usage.limit;
    }, [usage]);

    const handleEvaluate = async (id: number) => {
        try {
            const res = await evaluateSegment({ jwtToken, id }).unwrap();
            message.success(intl.formatMessage(
                { id: "segments.toast.evaluated", defaultMessage: "{n} matching customer(s)" },
                { n: res.matchingCustomerCount },
            ));
            refetch();
        } catch (err: any) {
            message.error(err?.data?.message || "Evaluate failed");
        }
    };

    const handleDelete = async (id: number) => {
        try {
            await deleteSegment({ jwtToken, id }).unwrap();
            message.success(intl.formatMessage({ id: "segments.toast.deleted", defaultMessage: "Segment deleted" }));
        } catch (err: any) {
            message.error(err?.data?.message || "Delete failed");
        }
    };

    const columns: ColumnsType<ISavedSegment> = [
        {
            title: intl.formatMessage({ id: "segments.col.name", defaultMessage: "Name" }),
            dataIndex: "name",
            key: "name",
            render: (name: string, record) => (
                <Space size={10}>
                    <span
                        style={{
                            display: "inline-block",
                            width: 10,
                            height: 10,
                            borderRadius: 5,
                            background: record.color ?? "#bfbfbf",
                            flexShrink: 0,
                        }}
                    />
                    <Space direction="vertical" size={0}>
                        <Text strong>{name}</Text>
                        {record.description && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {record.description}
                            </Text>
                        )}
                    </Space>
                </Space>
            ),
        },
        {
            title: intl.formatMessage({ id: "segments.col.criteria", defaultMessage: "Criteria" }),
            key: "criteria",
            render: (_, r) => {
                const customerCount = r.filterCriteria.length;
                const surveyCount = countSurveyCriteria(r.surveyCriteria);
                return (
                    <Space size={4} wrap>
                        {surveyCount > 0 && (
                            <Tooltip
                                title={intl.formatMessage({
                                    id: "segments.col.surveyTooltip",
                                    defaultMessage: "Survey-side criteria (cohort and/or country)",
                                })}
                            >
                                <Tag color="geekblue">
                                    {intl.formatMessage(
                                        { id: "segments.col.surveyTag", defaultMessage: "{n} survey" },
                                        { n: surveyCount },
                                    )}
                                </Tag>
                            </Tooltip>
                        )}
                        {customerCount > 0 && (
                            <Tooltip title={`${r.matchLogic.toUpperCase()} of ${customerCount}`}>
                                <Tag color="blue">
                                    {intl.formatMessage(
                                        { id: "segments.col.customerTag", defaultMessage: "{n} customer ({logic})" },
                                        { n: customerCount, logic: r.matchLogic },
                                    )}
                                </Tag>
                            </Tooltip>
                        )}
                    </Space>
                );
            },
        },
        {
            title: intl.formatMessage({ id: "segments.col.size", defaultMessage: "Estimated size" }),
            key: "size",
            render: (_, r) => {
                if (r.estimatedSize == null) {
                    return <Text type="secondary">—</Text>;
                }
                return (
                    <Space>
                        <Text>{r.estimatedSize}</Text>
                        {r.lastEvaluatedAt && (
                            <Text type="secondary" style={{ fontSize: 11 }}>
                                {new Date(r.lastEvaluatedAt).toLocaleString()}
                            </Text>
                        )}
                    </Space>
                );
            },
        },
        {
            title: intl.formatMessage({ id: "segments.col.actions", defaultMessage: "Actions" }),
            key: "actions",
            render: (_, r) => (
                <Space>
                    <Tooltip
                        title={
                            canCompareOnTrends
                                ? intl.formatMessage({
                                      id: "segments.actions.compareTooltip",
                                      defaultMessage:
                                          "Overlay this segment on the Trends chart (Segment Comparison tab — coming in Phase 2).",
                                  })
                                : intl.formatMessage({
                                      id: "segments.actions.compareLockedTooltip",
                                      defaultMessage:
                                          "Compare on Trends — available on Professional and above.",
                                  })
                        }
                    >
                        <Button
                            size="small"
                            icon={canCompareOnTrends ? <LineChartOutlined /> : <LockOutlined />}
                            onClick={() => {
                                if (canCompareOnTrends) {
                                    // Route to Trends with the Segment Comparison
                                    // tab active and this segment pre-selected.
                                    navigate(`/dashboard/analytics/trends?tab=compare&segmentId=${r.id}`);
                                } else {
                                    message.info(intl.formatMessage({
                                        id: "segments.actions.compareLockedMessage",
                                        defaultMessage:
                                            "Compare on Trends is available on Professional and above.",
                                    }));
                                }
                            }}
                        >
                            {intl.formatMessage({ id: "segments.actions.compare", defaultMessage: "Compare on Trends" })}
                        </Button>
                    </Tooltip>
                    <Button
                        size="small"
                        icon={<PlayCircleOutlined />}
                        loading={evalState.isLoading && evalState.originalArgs?.id === r.id}
                        onClick={() => handleEvaluate(r.id)}
                    >
                        {intl.formatMessage({ id: "segments.actions.evaluate", defaultMessage: "Evaluate" })}
                    </Button>
                    <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => {
                            setEditing(r);
                            setEditorOpen(true);
                        }}
                    />
                    <Popconfirm
                        title={intl.formatMessage({ id: "segments.confirmDelete", defaultMessage: "Delete this segment?" })}
                        onConfirm={() => handleDelete(r.id)}
                        okButtonProps={{ danger: true }}
                    >
                        <Button size="small" icon={<DeleteOutlined />} danger />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const newDisabled =
        // Disable if no fields AND no survey-side options either.
        (fields.length === 0 && cohorts.length === 0 && countries.length === 0) ||
        libraryFull;

    const newDisabledTooltip = libraryFull
        ? intl.formatMessage(
            {
                id: "segments.libraryFullTooltip",
                defaultMessage: "Saved segment limit reached ({used}/{limit}). Upgrade to add more.",
            },
            { used: usage?.used ?? 0, limit: usage?.limit ?? 0 },
          )
        : undefined;

    return (
        <>
            <Card
                style={{ background: colorBgContainer, borderRadius: borderRadiusLG }}
                title={
                    <Space>
                        <PartitionOutlined />
                        <Title level={4} style={{ margin: 0 }}>
                            {intl.formatMessage({ id: "segments.title", defaultMessage: "Customer segments" })}
                        </Title>
                    </Space>
                }
                extra={
                    <Space>
                        {usage && (
                            <Text type="secondary">
                                {usage.unlimited
                                    ? intl.formatMessage(
                                        { id: "segments.usage.unlimited", defaultMessage: "{used} saved" },
                                        { used: usage.used },
                                      )
                                    : intl.formatMessage(
                                        { id: "segments.usage.limited", defaultMessage: "{used} of {limit} used" },
                                        { used: usage.used, limit: usage.limit },
                                      )}
                            </Text>
                        )}
                        <Tooltip title={newDisabledTooltip}>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                disabled={newDisabled}
                                onClick={() => {
                                    setEditing(null);
                                    setEditorOpen(true);
                                }}
                            >
                                {intl.formatMessage({ id: "segments.newCta", defaultMessage: "New segment" })}
                            </Button>
                        </Tooltip>
                    </Space>
                }
            >
                <Paragraph type="secondary">
                    {intl.formatMessage({
                        id: "segments.intro",
                        defaultMessage:
                            "A segment combines survey-side criteria (cohort, country) and customer-side criteria (segmentation fields). Define a segment once and re-use it in cohort comparison and Trends analysis.",
                    })}
                </Paragraph>

                {fields.length === 0 && cohorts.length === 0 && countries.length === 0 && (
                    <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                        message={intl.formatMessage({
                            id: "segments.noOptions.title",
                            defaultMessage: "Nothing to segment on yet",
                        })}
                        description={intl.formatMessage({
                            id: "segments.noOptions.body",
                            defaultMessage:
                                "Define customer segmentation fields under the Fields tab, or run surveys with cohorts and countries set, to start segmenting.",
                        })}
                    />
                )}

                <Table<ISavedSegment>
                    rowKey="id"
                    loading={listLoading}
                    columns={columns}
                    dataSource={segments ?? []}
                    locale={{
                        emptyText: (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={intl.formatMessage({ id: "segments.empty", defaultMessage: "No saved segments yet" })}
                            />
                        ),
                    }}
                    pagination={false}
                />
            </Card>

            <SegmentEditorModal
                open={editorOpen}
                initial={editing}
                fields={fields}
                cohorts={cohorts}
                countries={countries}
                existingColours={(segments ?? []).map((s) => s.color)}
                saving={creating || updating}
                onClose={() => {
                    setEditorOpen(false);
                    setEditing(null);
                }}
                onSubmit={async (payload, id) => {
                    try {
                        if (id) {
                            await updateSegment({ jwtToken, id, payload }).unwrap();
                            message.success(intl.formatMessage({ id: "segments.toast.updated", defaultMessage: "Segment updated" }));
                        } else {
                            await createSegment({ jwtToken, payload }).unwrap();
                            message.success(intl.formatMessage({ id: "segments.toast.created", defaultMessage: "Segment created" }));
                        }
                        setEditorOpen(false);
                        setEditing(null);
                    } catch (err: any) {
                        message.error(err?.data?.message || "Save failed");
                    }
                }}
            />
        </>
    );
};

export default CustomerSegmentsPage;
